// =========================================================
// ONE place for all RoboMind courses.
// Used by the homepage (ClassesSection) and the Programs page (AllPrograms).
// To add a course (e.g. Roblox), add it here once.
// =========================================================

// Package groups — same colors as the pricing section
export const CATEGORIES = {
  standard: { label: 'Standard', color: '#F7931E', ink: '#0A1230' },
  mastery: { label: 'Mastery', color: '#C9CFD8', ink: '#0A1230' },
  robotics: { label: 'Robotics', color: '#3B7BFF', ink: '#FFFFFF' },
};

// text = short line (homepage list) · description = fuller text (Programs page)
export const COURSES = [
  {
    id: 'ai-creators', title: 'AI Creators', category: 'mastery', isNew: true, age: '9–17', sessions: 24,
    text: 'Turn ideas into finished creations with AI: stories, images, voice, music, videos, websites and games.',
    description:
      'Students turn their own ideas into finished creations with AI: characters, stories, images, voice, music, videos, websites and games. Skills first, tools second.',
  },
  {
    id: 'game-dev', title: 'Game Development', category: 'standard', age: '6–17', sessions: 16,
    text: 'Design and build interactive games from scratch with real game logic.',
    description: 'Design and build interactive games from scratch using real game logic and creativity.',
  },
  {
    id: 'mobile-apps', title: 'Mobile Applications', category: 'standard', age: '6–17', sessions: 16,
    text: 'Design and build real mobile apps with interactive features.',
    description: 'Design and develop real mobile apps with interactive features and user-friendly interfaces.',
  },
  {
    id: 'ai', title: 'Artificial Intelligence', category: 'standard', age: '6–17', sessions: 8,
    text: 'Discover how machines learn and build a first AI model.',
    description: 'Discover how machines think and learn while building a first intelligent system and AI model.',
  },
  {
    id: 'web-intro', title: 'Web Dev Introduction', category: 'standard', age: '6–17', sessions: 8,
    text: 'Build beautiful websites from zero with HTML and CSS.',
    description: 'Start the web development journey by building beautiful, responsive websites from zero with HTML and CSS.',
  },
  {
    id: 'web-advanced', title: 'Web Dev Advanced', category: 'standard', age: '9–17', sessions: 16,
    text: 'Advanced layouts, JavaScript and modern web techniques.',
    description: 'Take web skills further with advanced layouts, JavaScript, responsiveness and modern development techniques.',
  },
  {
    id: 'web-mastery', title: 'Web Mastery: PHP & MySQL', category: 'mastery', age: '9–17', sessions: 24,
    text: 'Dynamic websites with databases, logins and real backend logic.',
    description: 'Build powerful dynamic websites with databases, login systems and real backend logic.',
  },
  {
    id: 'python-intro', title: 'Python Introduction', category: 'standard', age: '6–17', sessions: 8,
    text: 'Start coding with Python in a fun, easy way.',
    description: 'Begin coding with Python and learn the basics of programming in a fun and easy way.',
  },
  {
    id: 'python-advanced', title: 'Python Advanced', category: 'standard', age: '9–17', sessions: 16,
    text: 'Functions, OOP, APIs and real-world Python projects.',
    description: 'Master advanced Python concepts like functions, object-oriented programming, APIs and real-world projects.',
  },
  {
    id: 'ai-python', title: 'AI & Python', category: 'mastery', age: '9–17', sessions: 24,
    text: 'Combine Python and AI to build smart systems and data projects.',
    description: 'Combine Python and artificial intelligence to build smart systems, data projects and real-world AI applications.',
  },
  {
    id: 'cpp', title: 'C++', category: 'standard', age: '9–17', sessions: 16,
    text: 'Powerful programming concepts and fast applications.',
    description: 'Learn powerful programming concepts and develop high-performance applications with C++.',
  },
  {
    id: 'java', title: 'Java', category: 'standard', age: '9–17', sessions: 16,
    text: 'Object-oriented programming and cross-platform apps.',
    description: 'Build cross-platform applications and understand object-oriented programming with Java.',
  },
  {
    id: 'cyber', title: 'Cyber Security', category: 'standard', age: '9–17', sessions: 8,
    text: 'Online safety, protecting systems and spotting threats.',
    description: 'Learn online safety, how to protect systems and detect threats, and the basics of digital security.',
  },
  {
    id: 'arduino', title: 'Electronics Arduino', category: 'standard', age: '6–17', sessions: 16,
    text: 'Real circuits and sensors, programmed with Arduino.',
    description: 'Create real electronic projects with circuits and sensors, and program Arduino boards.',
  },
  {
    id: 'microbit', title: 'Smart Robot micro:bit', category: 'robotics', age: '6–17', sessions: 16,
    text: 'Build and program a smart robot car, kit delivered home.',
    description: 'Build and program a smart robot car with Micro:bit and sensors. The robot kit is delivered to your home.',
  },
  {
    id: 'vex-vr', title: 'VEX VR Robot', category: 'standard', age: '9–17', sessions: 16,
    text: 'Program virtual robots in a VEXcode VR simulation.',
    description: 'Control and program virtual robots in a competitive, interactive simulation with VEXcode VR.',
  },
  {
    id: 'vr', title: 'Virtual Reality', category: 'standard', age: '6–17', sessions: 8,
    text: 'Create immersive worlds and interactive VR experiences.',
    description: 'Explore immersive worlds and create interactive virtual reality experiences with modern tools.',
  },
  {
    id: 'design', title: 'Design Thinking', category: 'standard', age: '6–17', sessions: 8,
    text: '3D design with Tinkercad: houses, drones and inventions.',
    description: 'Turn ideas into 3D designs with Tinkercad, creating real models like houses, cups, drones and inventions.',
  },
];

// Course paths: each step leads to the next
export const PATHS = [
  {
    id: 'python-path',
    title: 'Python & AI path',
    steps: ['python-intro', 'python-advanced', 'ai-python'],
  },
  {
    id: 'web-path',
    title: 'Web development path',
    steps: ['web-intro', 'web-advanced', 'web-mastery'],
  },
];

export const getCourse = (id) => COURSES.find((c) => c.id === id);
// Courses a parent can ask for on the free class form (and link a demo project to,
// in Admin > Curriculum > Demo sessions). minAge hides a course for younger children.
// Keep these names: they are saved with each demo request.
export const DEMO_COURSES = [
  { title: 'Game Development', minAge: 6 },
  { title: 'Mobile Applications', minAge: 6 },
  { title: 'Python', minAge: 6 },
  { title: 'Web Development', minAge: 6 },
  { title: 'AI Creators', minAge: 9 },
  { title: 'C++', minAge: 9 },
  { title: 'Java', minAge: 9 },
];
